import React, { FC } from 'react';

// material-ui
import { Popover } from '@mui/material';

import styled from 'styled-components';

// theme
import aampeTheme from '../../constants/aampeTheme';
// constants
import ICONS from '../../constants/icons';

// hooks
import useAnalytics from '../../hooks/helper/useAnalytics';
import useGetVideoDuration from '../../hooks/helper/useGetVideoDuration';

// models
import { LearnTypeDataModel } from '../../models/learn.model';

// components
import BasicButton from '../buttons/basicButton';

interface LearnVideoPreviewPopupProps {
    anchorEl: Element | null;
    handleClose: () => void;
    learnData: LearnTypeDataModel;
    setVideoIsOpen: (value: boolean) => void;
}

const LearnVideoPreviewPopup: FC<
    React.PropsWithChildren<LearnVideoPreviewPopupProps>
> = ({ anchorEl, handleClose, setVideoIsOpen, learnData }) => {
    const { track } = useAnalytics();

    const { data: videoDuration } = useGetVideoDuration(learnData.embedId);

    const handleWatchClick = () => {
        setVideoIsOpen(true);
        track('watch_button_clicked', {
            page: learnData?.title,
        });
        handleClose();
    };

    return (
        <StyledPopover
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
        >
            <Container>
                <Header>
                    <ICONS.learnVideo color={aampeTheme.colors.solidA01} />
                    <Title>{learnData.title}</Title>
                    <Duration>{videoDuration}</Duration>
                </Header>
                <Image
                    src={`https://img.youtube.com/vi/${learnData.embedId}/0.jpg`}
                    alt="Click watch to view video"
                />
                <Description>{learnData.description}</Description>
                <Button>
                    <BasicButton
                        name="Watch"
                        isPrimary
                        style={{ width: '85%' }}
                        action={handleWatchClick}
                    />
                </Button>
            </Container>
        </StyledPopover>
    );
};

const StyledPopover = styled(Popover)({
    marginLeft: aampeTheme.spacing(-3),
});

const Container = styled.div({
    padding: aampeTheme.spacing(2),
    border: `2px solid ${aampeTheme.colors.solidA01}`,
    width: '320px',
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(2),
    borderRadius: aampeTheme.borderRadius.default,
});

const Header = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing(2),
    alignItems: 'center',
});

const Title = styled.span({
    color: aampeTheme.colors.gray800,
    fontWeight: 600,
});

const Image = styled.img({
    width: '100%',
    borderRadius: aampeTheme.borderRadius.default,
    height: '190px',
});

const Description = styled.p({
    fontSize: '12px',
    color: aampeTheme.colors.gray800,
});

const Button = styled.div({
    display: 'flex',
    justifyContent: 'center',
});

const Duration = styled.span({
    color: aampeTheme.colors.gray800,
    fontWeight: 600,
    fontSize: '14px',
});

export default LearnVideoPreviewPopup;
